import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { OutlineButtonComponent } from '../../../../shared/components/outline-button/outline-button';
import { PrimaryButtonComponent } from '../../../../shared/components/primary-button/primary-button';
import {
  QuestionResultApiData,
  QuestionResultApiOption,
  ReviewQuestionStatus,
  SectionQuestionMapApiSection,
} from '../../model/test-series.model';
import { TestSeriesService } from '../../services/test-series.service';
import { BookmarkService } from '../../services/bookmark.service';
import { embedImagesInHtml, normalizeImageUrls, splitExplanationImages, stripHtmlTags } from '../../../../shared/utils/question-html.util';
import { sanitizeRichHtml } from '../../../../shared/utils/rich-html.util';
import { RichHtmlPipe } from '../../../../shared/pipes/rich-html.pipe';
import { AnswerJourneyComponent } from '../../components/answer-journey/answer-journey';
import { AnswerJourneyView, buildAnswerJourney } from '../../components/answer-journey/answer-journey.mapper';

interface PaletteQuestion {
  questionNumber: number;
  questionId: string;
  status: ReviewQuestionStatus;
  prompt: string;
  subject: string;
  isBookmarked: boolean;
  isNotVisited: boolean;
  isGuessed: boolean;
  markedForReview: boolean;
}

interface PaletteSection {
  id: string;
  label: string;
  questions: PaletteQuestion[];
}

interface ActiveQuestionDetail {
  questionNumber: number;
  questionId: string;
  question: string;
  subject: string;
  chapter: string;
  topic: string;
  explanation: string;
  difficultyLevel: string;
  status: ReviewQuestionStatus;
  isSkipped: boolean;
  markedForReview: boolean;
  isNotVisited: boolean;
  isGuessed: boolean;
  options: ActiveQuestionOption[];
  questionImageUrls: string[];
  explanationImageUrls: string[];
  reference?: string | null;
  journey: AnswerJourneyView | null;
}

interface ActiveQuestionOption {
  key: 'A' | 'B' | 'C' | 'D';
  text: string;
  isSelected: boolean;
  isCorrect: boolean;
}

@Component({
  selector: 'app-exam-review',
  imports: [AnswerJourneyComponent, OutlineButtonComponent, PrimaryButtonComponent, RichHtmlPipe],
  templateUrl: './exam-review.html',
  styleUrl: './exam-review.css',
})
export class ExamReviewComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly testSeriesService = inject(TestSeriesService);
  private readonly bookmarkService = inject(BookmarkService);
  private readonly toastr = inject(ToastrService);
  private readonly domSanitizer = inject(DomSanitizer);

  protected readonly activePartIndex = signal(0);
  protected readonly paletteSections = signal<PaletteSection[]>([]);
  protected readonly isPaletteLoading = signal(true);
  protected readonly isQuestionLoading = signal(false);
  protected readonly isPaletteCollapsed = signal(false);
  protected readonly activeQuestionDetail = signal<ActiveQuestionDetail | null>(null);
  protected readonly activeQuestionNumber = signal<number>(0);
  // Question/explanation HTML from the API carries inline styles (tables, image sizing)
  // that Angular's default [innerHTML] sanitizer strips, so sanitize it ourselves and trust it.
  protected readonly questionHtml = computed(() => this.toRichHtml(this.activeQuestionDetail()?.question));
  protected readonly explanationHtml = computed(() => this.toRichHtml(this.activeQuestionDetail()?.explanation));
  protected readonly bookmarkedQuestionIds = signal<string[]>([]);
  protected readonly bookmarkingQuestionId = signal<string | null>(null);
  protected readonly examName = signal<string>('');

  /** Flat list of all questions across all sections, for prev/next navigation */
  protected readonly allPaletteQuestions = computed(() =>
    this.paletteSections().flatMap((section) => section.questions),
  );

  protected readonly totalQuestionCount = computed(() =>
    this.allPaletteQuestions().length,
  );

  protected readonly activePaletteSection = computed(() =>
    this.paletteSections()[this.activePartIndex()] ?? null,
  );

  protected readonly activePaletteQuestions = computed(() =>
    this.activePaletteSection()?.questions ?? [],
  );

  protected readonly paletteParts = computed(() =>
    this.paletteSections().map((section) => ({
      id: section.id,
      label: section.label,
    })),
  );

  protected readonly correctCount = computed(() =>
    this.allPaletteQuestions().filter((q) => q.status === 'correct').length,
  );

  protected readonly incorrectCount = computed(() =>
    this.allPaletteQuestions().filter((q) => q.status === 'incorrect').length,
  );

  protected readonly skippedCount = computed(() =>
    this.allPaletteQuestions().filter((q) => q.status === 'skipped' && !q.isNotVisited).length,
  );

  protected readonly notVisitedCount = computed(() =>
    this.allPaletteQuestions().filter((q) => q.isNotVisited).length,
  );

  protected readonly markedForReviewCount = computed(() =>
    this.allPaletteQuestions().filter((q) => q.markedForReview).length,
  );

  protected readonly guessedCount = computed(() =>
    this.allPaletteQuestions().filter((q) => q.isGuessed).length,
  );

  protected readonly statusPills = computed(() => [
    {
      id: 'correct',
      label: 'Correct',
      count: this.correctCount(),
      toneClass: 'review-tone-correct',
    },
    {
      id: 'incorrect',
      label: 'Incorrect',
      count: this.incorrectCount(),
      toneClass: 'review-tone-incorrect',
    },
    {
      id: 'skipped',
      label: 'Skipped',
      count: this.skippedCount(),
      toneClass: 'review-tone-skipped',
    },
    {
      id: 'not-visited',
      label: 'Not Visited',
      count: this.notVisitedCount(),
      toneClass: 'review-tone-not-visited',
    },
    {
      id: 'marked',
      label: 'Marked for Review',
      count: this.markedForReviewCount(),
      toneClass: 'review-tone-marked',
    },
    {
      id: 'guessed',
      label: 'Guessed',
      count: this.guessedCount(),
      toneClass: 'review-tone-guessed',
    },
  ]);

  /** Index of active question within the flat allPaletteQuestions list */
  protected readonly activeGlobalIndex = computed(() => {
    const num = this.activeQuestionNumber();
    return this.allPaletteQuestions().findIndex((q) => q.questionNumber === num);
  });

  protected readonly canGoPrevious = computed(() => this.activeGlobalIndex() > 0);

  protected readonly canGoNext = computed(() => {
    const idx = this.activeGlobalIndex();
    return idx >= 0 && idx < this.allPaletteQuestions().length - 1;
  });

  protected readonly isBookmarked = computed(() => {
    const questionId = this.activeQuestionDetail()?.questionId;

    return questionId ? this.bookmarkedQuestionIds().includes(questionId) : false;
  });

  private origin: 'dashboard' | 'test-series' | 'performance' = 'test-series';
  protected readonly showExamSummary = signal(true);

  ngOnInit(): void {
    const fromParam = this.route.snapshot.queryParamMap.get('from');
    this.origin = fromParam === 'dashboard' || fromParam === 'performance' ? fromParam : 'test-series';
    this.showExamSummary.set(this.origin !== 'performance');

    this.loadSectionQuestionMap();
  }

  /** Returns an openable URL when the reference is a web link, otherwise null. */
  protected referenceUrl(reference: string | null | undefined): string | null {
    const value = reference?.trim();
    if (!value || /\s/.test(value)) return null;
    if (/^https?:\/\//i.test(value)) return value;
    if (/^www\./i.test(value)) return `https://${value}`;
    return null;
  }

  protected goBack(): void {
    if (this.origin === 'performance') {
      const examId = this.route.snapshot.paramMap.get('id');
      const attemptId = this.route.snapshot.queryParamMap.get('attemptId');
      void this.router.navigate(
        examId ? ['/test-series/performance/tests', examId] : ['/test-series/performance'],
        { queryParams: examId && attemptId ? { attemptId } : {} },
      );
      return;
    }

    void this.router.navigate([this.origin === 'dashboard' ? '/dashboard' : '/test-series']);
  }

  protected goToExamSummary(): void {
    const examId = this.route.snapshot.paramMap.get('id');

    if (examId) {
      void this.router.navigate(['/test-series', examId, 'exam-summary'], { queryParams: { from: this.origin } });
    }
  }

  protected selectPartByIndex(index: number): void {
    if (index < 0 || index >= this.paletteSections().length) {
      return;
    }

    this.activePartIndex.set(index);
    const firstQuestionInPart = this.paletteSections()[index]?.questions[0];

    if (firstQuestionInPart) {
      this.onPaletteQuestionClick(firstQuestionInPart);
    }
  }

  protected bookmarkActiveQuestion(): void {
    const questionId = this.activeQuestionDetail()?.questionId;

    if (!questionId) {
      return;
    }

    const examId = this.route.snapshot.paramMap.get('id');

    if (!examId) {
      return;
    }

    this.createBookmark(examId, questionId);
  }

  protected bookmarkQuestion(questionId: string, event?: Event): void {
    event?.stopPropagation();
    const examId = this.route.snapshot.paramMap.get('id');

    if (!examId) {
      return;
    }

    this.createBookmark(examId, questionId);
  }

  protected isQuestionBookmarked(questionId: string): boolean {
    return this.bookmarkedQuestionIds().includes(questionId);
  }

  protected isQuestionBookmarkLoading(questionId: string): boolean {
    return this.bookmarkingQuestionId() === questionId;
  }

  protected onPaletteQuestionClick(question: PaletteQuestion): void {
    this.switchToPart(question.questionNumber);
    this.activeQuestionNumber.set(question.questionNumber);
    this.loadQuestionResult(question.questionId, question.questionNumber);

    if (window.innerWidth < 1280) {
      return;
    }

    setTimeout(() => {
      const el = document.getElementById(`sidebar-question-${question.questionNumber}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }, 50);
  }

  protected showPreviousQuestion(): void {
    const idx = this.activeGlobalIndex();

    if (idx <= 0) {
      return;
    }

    const prev = this.allPaletteQuestions()[idx - 1];
    this.onPaletteQuestionClick(prev);
  }

  protected showNextQuestion(): void {
    const idx = this.activeGlobalIndex();
    const all = this.allPaletteQuestions();

    if (idx < 0 || idx >= all.length - 1) {
      return;
    }

    const next = all[idx + 1];
    this.onPaletteQuestionClick(next);
  }

  protected getStatusTone(status: ReviewQuestionStatus): string {
    if (status === 'correct') {
      return 'review-tone-correct';
    }

    if (status === 'incorrect') {
      return 'review-tone-incorrect';
    }

    return 'review-tone-skipped';
  }

  protected getPaletteButtonClass(question: PaletteQuestion): string {
    const isActive = question.questionNumber === this.activeQuestionNumber();
    const base = 'review-palette-base';

    if (question.isNotVisited) {
      return `${base} ${isActive ? 'review-palette-active-not-visited' : 'review-palette-not-visited'}`;
    }

    if (question.markedForReview) {
      return `${base} ${isActive ? 'review-palette-active-marked' : 'review-palette-marked'}`;
    }

    if (question.isGuessed) {
      return `${base} ${isActive ? 'review-palette-active-guessed' : 'review-palette-guessed'}`;
    }

    if (question.status === 'correct') {
      return `${base} ${isActive ? 'review-palette-active-correct' : 'review-palette-correct'}`;
    }

    if (question.status === 'incorrect') {
      return `${base} ${isActive ? 'review-palette-active-incorrect' : 'review-palette-incorrect'}`;
    }

    return `${base} ${isActive ? 'review-palette-active-skipped' : 'review-palette-skipped'}`;
  }

  protected getSidebarCardClass(questionNumber: number): string {
    return questionNumber === this.activeQuestionNumber()
      ? 'review-sidebar-card review-sidebar-card-active'
      : 'review-sidebar-card';
  }

  protected isPartActive(index: number): boolean {
    return index === this.activePartIndex();
  }

  protected getOptionClass(option: ActiveQuestionOption): string {
    if (option.isCorrect) {
      return 'review-option review-option-correct';
    }

    if (option.isSelected && !option.isCorrect) {
      return 'review-option review-option-incorrect';
    }

    return 'review-option';
  }

  protected getOptionLabelClass(option: ActiveQuestionOption): string {
    if (option.isCorrect) {
      return 'review-option-label review-option-label-correct';
    }

    if (option.isSelected && !option.isCorrect) {
      return 'review-option-label review-option-label-incorrect';
    }

    return 'review-option-label';
  }

  protected getOptionLetter(index: number): string {
    return String.fromCharCode(65 + index);
  }

  private switchToPart(questionNumber: number): void {
    const sections = this.paletteSections();

    for (let i = 0; i < sections.length; i++) {
      if (sections[i].questions.some((q) => q.questionNumber === questionNumber)) {
        this.activePartIndex.set(i);
        return;
      }
    }
  }

  private loadSectionQuestionMap(selectedQuestionId?: string): void {
    const examId = this.route.snapshot.paramMap.get('id');

    if (!examId) {
      this.isPaletteLoading.set(false);
      return;
    }

    this.testSeriesService.getSectionQuestionMap(examId).subscribe({
      next: (response) => {
        if (response?.code === '200' && response.data) {
          const sectionsData = Array.isArray(response.data) ? response.data : (response.data.sections || []);

          if (!Array.isArray(response.data) && response.data.exam_name) {
            this.examName.set(response.data.exam_name);
          }

          const sections = sectionsData.map(
            (section, index) => this.mapApiSectionToPalette(section, index),
          );
          this.paletteSections.set(sections);
          this.bookmarkedQuestionIds.set(
            sections
              .flatMap((section) => section.questions)
              .filter((question) => question.isBookmarked)
              .map((question) => question.questionId),
          );

          const targetQuestion = selectedQuestionId
            ? sections
              .flatMap((section) => section.questions)
              .find((question) => question.questionId === selectedQuestionId)
            : null;
          const firstQuestion = sections[0]?.questions[0];

          if (targetQuestion) {
            this.onPaletteQuestionClick(targetQuestion);
          } else if (firstQuestion) {
            this.onPaletteQuestionClick(firstQuestion);
          }
        }

        this.isPaletteLoading.set(false);
      },
      error: () => {
        this.isPaletteLoading.set(false);
      },
    });
  }

  private loadQuestionResult(questionId: string, questionNumber: number): void {
    const examId = this.route.snapshot.paramMap.get('id');

    if (!examId) {
      return;
    }

    this.isQuestionLoading.set(true);

    this.testSeriesService.getQuestionResult(examId, questionId).subscribe({
      next: (response) => {
        if (response?.code === '200' && response.data) {
          this.activeQuestionDetail.set(
            this.mapApiQuestionToDetail(response.data, questionNumber),
          );
          this.syncBookmarkedQuestion(questionId, response.data.is_bookmarked ?? false);
        }

        this.isQuestionLoading.set(false);
      },
      error: () => {
        this.isQuestionLoading.set(false);
      },
    });
  }

  private toRichHtml(html: string | null | undefined): SafeHtml | null {
    const sanitized = sanitizeRichHtml(html);
    return sanitized ? this.domSanitizer.bypassSecurityTrustHtml(sanitized) : null;
  }

  private mapApiSectionToPalette(section: SectionQuestionMapApiSection, index: number): PaletteSection {
    return {
      id: `part-${index}`,
      label: section.section_name,
      questions: section.questions.map((q) => ({
        questionNumber: q.question_number,
        questionId: q.question_id,
        status: this.mapApiStatus(q.status),
        prompt: stripHtmlTags(q.question || ''),
        subject: q.subject || 'Subject N/A',
        isBookmarked: q.is_bookmarked ?? false,
        isNotVisited: q.is_not_visited ?? false,
        isGuessed: q.is_guessed ?? false,
        markedForReview: q.is_marked_for_review ?? q.marked_for_review ?? false,
      })),
    };
  }

  private mapApiQuestionToDetail(
    data: QuestionResultApiData,
    questionNumber: number,
  ): ActiveQuestionDetail {
    // Question images go into their <img> placeholders in the HTML (e.g. a table cell);
    // only URLs without a placeholder are rendered below the content. Explanation HTML
    // keeps its own <img> src; its image URL array is rendered below it.
    const question = embedImagesInHtml(data.question, normalizeImageUrls(data.question_image_url));
    const explanation = splitExplanationImages(data.explanation, normalizeImageUrls(data.explanation_image_url));

    return {
      questionNumber,
      questionId: data.question_id,
      question: question.html,
      subject: data.subject,
      chapter: data.chapter,
      topic: data.topic,
      explanation: explanation.html,
      difficultyLevel: data.difficulty_level,
      status: this.deriveStatusFromResult(data),
      isSkipped: data.is_skipped ?? false,
      markedForReview: data.is_marked_for_review ?? data.marked_for_review ?? false,
      isNotVisited: data.is_not_visited ?? false,
      isGuessed: data.is_guessing ?? data.is_guessed ?? false,
      options: data.options.map((opt) => this.mapApiOption(opt)),
      questionImageUrls: question.remainingImageUrls,
      explanationImageUrls: explanation.remainingImageUrls,
      reference: data.reference?.trim() || null,
      journey: buildAnswerJourney(data),
    };
  }

  private deriveStatusFromResult(data: QuestionResultApiData): ReviewQuestionStatus {
    if (data.is_correct) {
      return 'correct';
    }

    if (data.is_skipped) {
      return 'skipped';
    }

    return 'incorrect';
  }

  protected getQuestionBadges(): { label: string; cssClass: string }[] {
    const detail = this.activeQuestionDetail();
    if (!detail) {
      return [];
    }

    const badges: { label: string; cssClass: string }[] = [];

    if (detail.isNotVisited) {
      badges.push({ label: 'Not Visited', cssClass: 'border border-slate-300 text-slate-500 rounded-md bg-white' });
    } else if (detail.status === 'correct') {
      badges.push({ label: 'Correct', cssClass: 'review-tone-correct review-status-badge' });
    } else if (detail.status === 'incorrect') {
      badges.push({ label: 'Incorrect', cssClass: 'review-tone-incorrect review-status-badge' });
    } else {
      badges.push({ label: 'Skipped', cssClass: 'review-tone-skipped review-status-badge' });
    }

    if (detail.markedForReview) {
      badges.push({ label: 'Marked for Review', cssClass: 'border border-purple-500 text-purple-600 rounded-md bg-white' });
    }

    if (detail.isGuessed) {
      badges.push({ label: 'Guessed', cssClass: 'border border-sky-500 text-sky-600 rounded-md bg-white' });
    }

    return badges;
  }

  protected getSidebarBadges(question: PaletteQuestion): { label: string; dotClass: string; bgClass: string; textClass: string }[] {
    const badges: { label: string; dotClass: string; bgClass: string; textClass: string }[] = [];

    if (question.isNotVisited) {
      badges.push({ label: 'Not Visited', dotClass: 'bg-slate-300', bgClass: 'bg-transparent', textClass: 'text-slate-500' });
    } else if (question.status === 'correct') {
      badges.push({ label: 'Correct', dotClass: 'bg-mock-green-600', bgClass: 'bg-transparent', textClass: 'text-text-secondary' });
    } else if (question.status === 'incorrect') {
      badges.push({ label: 'Incorrect', dotClass: 'bg-mock-red-500', bgClass: 'bg-transparent', textClass: 'text-text-secondary' });
    } else {
      badges.push({ label: 'Skipped', dotClass: 'bg-mock-amber-500', bgClass: 'bg-transparent', textClass: 'text-text-secondary' });
    }

    if (question.markedForReview) {
      badges.push({ label: 'Marked for Review', dotClass: 'hidden', bgClass: 'bg-purple-50 px-1.5 py-0.5 rounded', textClass: 'text-purple-600' });
    }

    if (question.isGuessed) {
      badges.push({ label: 'Guessed', dotClass: 'hidden', bgClass: 'bg-sky-50 px-1.5 py-0.5 rounded', textClass: 'text-sky-600' });
    }

    return badges;
  }

  private mapApiOption(option: QuestionResultApiOption): ActiveQuestionOption {
    return {
      key: option.key,
      text: option.text,
      isSelected: option.is_selected,
      isCorrect: option.is_correct,
    };
  }

  private mapApiStatus(status: string): ReviewQuestionStatus {
    if (status === 'CORRECT') {
      return 'correct';
    }

    if (status === 'WRONG') {
      return 'incorrect';
    }

    return 'skipped';
  }

  private createBookmark(examId: string, questionId: string): void {
    if (this.bookmarkingQuestionId()) {
      return;
    }

    this.bookmarkingQuestionId.set(questionId);

    this.bookmarkService.createBookmark(examId, questionId).subscribe({
      next: (response) => {
        this.addBookmarkedQuestion(questionId);
        this.toastr.success(response.message || 'Question bookmarked successfully');
        this.bookmarkingQuestionId.set(null);
        this.loadSectionQuestionMap(questionId);
      },
      error: () => {
        this.bookmarkingQuestionId.set(null);
      },
    });
  }

  private addBookmarkedQuestion(questionId: string): void {
    this.bookmarkedQuestionIds.update((ids) => (
      ids.includes(questionId) ? ids : [...ids, questionId]
    ));
  }

  private syncBookmarkedQuestion(questionId: string, isBookmarked: boolean): void {
    if (isBookmarked) {
      this.addBookmarkedQuestion(questionId);
      return;
    }

    this.bookmarkedQuestionIds.update((ids) => ids.filter((id) => id !== questionId));
  }
}
